<template>
  <div
    class="product-card"
    :data-product-id="id"
    :data-product-name="productName"
    :data-product-brand="brandName"
  >
    <div class="product-card-details">
      <div class="product-card-ad">
        <Ad
          v-if="ad.title"
          :title="ad.title"
          :option-type-text="ad.optionTypeText"
          :icon-type="ad.iconType"
        />
      </div>
      <div
        v-if="earlyAccessBadge"
        class="product-card-early-access"
        :style="{ opacity: featuredVariant.soldOut ? '50%' : '100%' }"
      >
        <span>{{ earlyAccessBadge }}</span>
      </div>
      <slot
        :pdpUrl="pdpUrl"
        :alt="productName"
        :featuredVariant="featuredVariant"
        :placeholderVariantImage="placeholderVariantImage"
      >
        <a :href="pdpUrl" class="product-card-image-link">
          <img
            class="product-card-image"
            :src="featuredVariant.imageUrl || placeholderVariantImage"
            :alt="productName"
          />
        </a>
      </slot>
      <div v-if="featuredVariant.soldOut" class="product-card-oos-banner">
        {{ oosText }}
      </div>
      <div v-if="hasWishlist" class="product-card-add-to-wishlist">
        <a v-if="isFeaturedVariantWishlisted && isLoggedIn" href="/wishlist">
          <Icons
            name="heart"
            class="icons-filled"
            :style="`--icon-size: 40px;`"
          />
        </a>
        <Icons
          v-else
          name="heart"
          :is-clickable="true"
          :style="`--icon-size: 40px;`"
          @clicked="addToWishlist"
        />
      </div>
      <div class="product-card-cta">
        <Button
          v-if="showRemindMe"
          :label="remindMeText"
          :type="buttonType"
          size="is-fullwidth"
          font-size="14px"
          class="remind-me-btn"
          @click="remindMe"
        />
        <Button
          v-else-if="featuredVariant.soldOut"
          :label="
            isFeaturedVariantWaitlisted ? waitlistedText : addToWaitlistText
          "
          :type="buttonType"
          size="is-fullwidth"
          font-size="14px"
          class="waitlist-btn"
          @click="addToWaitlist"
        />
        <Button
          v-else-if="showAtb"
          :label="addToBagText"
          :type="buttonType"
          size="is-fullwidth"
          font-size="14px"
          class="add-to-bag-btn"
          @click="addToBag"
        />
      </div>
    </div>
    <slot
      name="description"
      :pdpUrl="pdpUrl"
      :brandName="brandName"
      :productName="productName"
      :rating="rating"
      :variantsCountText="variantsCountText"
      :featuredVariant="featuredVariant"
      :hasManyVariants="hasManyVariants"
      :label="label"
      :reviewsCount="reviewsCount"
    >
      <a class="product-card-description" :href="pdpUrl">
        <div class="labels">
          <span v-if="label" class="label">{{ label }}</span>
        </div>
        <div class="brand">{{ brandName }}</div>
        <div class="product-name">{{ productName }}</div>
        <Price
          :original-price="featuredVariant.originalPrice"
          :price="featuredVariant.price"
          :sale-text="featuredVariant.saleText"
          :on-sale="featuredVariant.onSale"
        />
        <StarRating v-if="rating" :rating="rating" />
        <span v-if="showReviewsCount" class="reviews-count">
          ({{ reviewsCount }})
        </span>
        <div v-if="hasManyVariants" class="variants-count">
          {{ variantsCountText }}
        </div>
      </a>
    </slot>
  </div>
</template>

<script>
import Button from '@sephora-asia/core-button/Button.vue'
import Price from '@sephora-asia/core-price/Price.vue'
import StarRating from '@sephora-asia/core-star-rating/StarRating.vue'
import Ad from '@sephora-asia/core-ad/Ad.vue'
import Icons from '@sephora-asia/atoms-icons/Icons.vue'
import variantPlaceHolderImg from './assets/variant-placeholder-image.svg'

const LaunchStatus = {
  Preview: 'preview',
  Prelaunch: 'prelaunch',
  Postlaunch: 'postlaunch'
}

export default {
  name: 'ProductCard',
  components: {
    Button,
    Price,
    StarRating,
    Ad,
    Icons
  },
  props: {
    /**
     * Product ID
     *
     * @type {string}
     */
    id: {
      type: String,
      required: true
    },
    /**
     * Product name
     *
     * @type {string}
     */
    productName: {
      type: String,
      required: true
    },
    /**
     * Product's brand name
     *
     * @type {string}
     */
    brandName: {
      type: String,
      required: true
    },
    /**
     * Link to product detail page
     *
     * @type {string}
     */
    pdpUrl: {
      type: String,
      required: true
    },
    /**
     * Add to Bag translation for CTA
     *
     * @type {string}
     */
    addToBagText: {
      type: String,
      required: true
    },
    /**
     * To show wishlist or not
     *
     * @type {boolean}
     */
    hasWishlist: {
      type: Boolean,
      default: true
    },
    /**
     * Add to Waitlist translation for CTA
     *
     * @type {string}
     */
    addToWaitlistText: {
      type: String,
      required: true
    },
    /**
     * Waitlisted translation for CTA
     *
     * @type {string}
     */
    waitlistedText: {
      type: String,
      required: true
    },
    /**
     * Out of stock banner text
     *
     * @type {string}
     */
    oosText: {
      type: String,
      required: true
    },
    /**
     * Product rating score
     *
     * @type {number}
     */
    rating: {
      type: Number,
      required: true
    },
    /**
     * Product option type. This can be these values: 'shade', 'size'
     *
     * @type {string}
     */
    optionType: {
      type: String,
      default: '',
      validator: function (value) {
        return ['shade', 'size', 'type', ''].includes(value.toLowerCase())
      }
    },
    /**
     * New label text
     *
     * @type {string}
     */
    label: {
      type: String,
      required: true
    },
    /**
     * Is CTA button disabled?
     *
     * @type {boolean}
     */
    isCtaDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Ad object with keys: title, optionTypeText(e.g. Selected sizes only), variantIds, isVirtualBundle
     *
     * @type {object}
     */
    ad: {
      type: Object,
      default: (value) => ({})
    },
    /**
     * An Array of wishlisted variant IDs
     *
     * @type {Array}
     */
    wishlistedVariantIds: {
      type: Array,
      default: (value) => []
    },
    /**
     * An Array of waitlisted variant IDs
     *
     * @type {Array}
     */
    waitlistedVariantIds: {
      type: Array,
      default: (value) => []
    },
    /**
     * Whether user is logged in or not. This is to determine to redirect to /wishlist or return the data to let
     *  consumer component decide what to do
     *
     * @type {boolean}
     */
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    /**
     * Number of reviews this product has
     *
     * @type {number}
     */
    reviewsCount: {
      type: Number,
      default: 0
    },
    /**
     * Number of variants
     *
     * @type {number}
     */
    variantsCount: {
      type: Number,
      default: 1
    },
    /**
     * Featured variant object
     * variant object need to contain properties:
     * id
     * slugUrl
     * name
     * originalPrice
     * price
     * saleText
     * onSale
     * imageUrl
     * soldOut
     *
     * @type {object}
     */
    featuredVariant: {
      type: Object,
      required: true
    },
    /**
     * Early access badge
     *
     * @type {string}
     */
    earlyAccessBadge: {
      type: String,
      default: ''
    },
    /**
     * To show Add to Bag button or not
     *
     * @type {boolean}
     */
    showAtb: {
      type: Boolean,
      default: true
    },
    /**
     * To show Remind Me button
     *
     * @type {boolean}
     */
    showRemindMe: {
      type: Boolean,
      default: false
    },
    /**
     * Remind me text
     *
     * @type {string}
     */
    remindMeText: {
      type: String,
      default: ''
    },
    /**
     * An array of early access notification subscribed variant IDs
     *
     * @type {Array}
     */
    notificationSubscribedVariantIds: {
      type: Array,
      default: (value) => []
    }
  },
  computed: {
    hasManyVariants() {
      return this.variantsCount > 1
    },
    isFeaturedVariantWishlisted() {
      return this.wishlistedVariantIds?.includes(
        Number(this.featuredVariant.id)
      )
    },
    isFeaturedVariantWaitlisted() {
      return this.waitlistedVariantIds?.includes(
        Number(this.featuredVariant.id)
      )
    },
    variantsCountText() {
      const optionType = this.optionType || 'type'
      return `${this.variantsCount} ${optionType}s`
    },
    buttonType() {
      if (
        this.isCtaDisabled ||
        (this.featuredVariant.soldOut && this.isFeaturedVariantWaitlisted) ||
        (this.isFeaturedVariantEASubscribed && !this.showAtb)
      ) {
        return 'btn-disabled'
      }
      if (this.featuredVariant.soldOut || this.showRemindMe) {
        return 'btn-default'
      }

      return 'btn-primary'
    },
    placeholderVariantImage() {
      return this.$options.locals.variantPlaceHolderImg
    },
    showReviewsCount() {
      return this.rating > 0 && this.reviewsCount > 0
    },
    isFeaturedVariantEASubscribed() {
      return this.notificationSubscribedVariantIds?.includes(
        Number(this.featuredVariant.id)
      )
    }
  },
  methods: {
    addToBag() {
      this.$emit('add-to-bag', {
        productId: this.id,
        variantId: this.featuredVariant.id
      })
    },
    addToWaitlist() {
      this.$emit('add-to-waitlist', {
        productId: this.id,
        variantId: this.featuredVariant.id
      })
    },
    addToWishlist() {
      this.$emit('add-to-wishlist', {
        productId: this.id,
        variantId: this.featuredVariant.id
      })
    },
    isAdAvailable(variantId) {
      // using == instead of === cos id's type returning from database is inconsistent, it's either String or Number
      return this.ad?.variantIds?.some((id) => id == variantId) || false
    },
    remindMe() {
      this.$emit('remind-me', {
        variantId: this.featuredVariant.id
      })
    }
  },
  locals: {
    variantPlaceHolderImg
  }
}
</script>

<style lang="scss" scoped>
.product-card {
  outline: 0;

  &:hover {
    .product-card-add-to-wishlist,
    .product-card-cta {
      @include widescreen {
        display: block;
      }
    }
  }

  /deep/ .stars {
    display: inline;
  }
}

.product-card-details {
  position: relative;

  .product-card-image-link {
    display: block;
    width: 100%;
    position: relative;

    &:before {
      content: '';
      display: block;
      padding-top: 100%; /* initial ratio of 1:1*/
    }

    /deep/ .product-card-image {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
    }
  }
}

.product-card-oos-banner {
  width: 100%;
  height: 45px;
  line-height: 50px;
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  font-weight: 700;
  font-size: 16px;
  text-align: center;
  text-transform: uppercase;
  color: $grey-darker;
  background-color: rgba($white, 0.75);
}

.product-card-add-to-wishlist {
  position: absolute;
  bottom: 0;
  right: 0;

  .icon-container {
    &::v-deep .icons-heart {
      --icon-hover-stroke-color: #{$red};
    }
    &.icons-filled {
      &::v-deep .icons-heart {
        --icon-fill-color: #{$red};
        --icon-stroke-color: #{$red};
        --icon-hover-fill-color: #{$red};
      }
    }
  }

  @include widescreen {
    display: none;
    top: 0;
  }
}

.product-card-cta {
  display: none;
  position: absolute;
  bottom: 0;
  width: 100%;
}

.product-card-description {
  display: block;
  margin-top: 7px;
  text-align: left;

  &:hover {
    color: $black;
  }

  .brand,
  .product-name,
  .variants-count,
  /deep/ .prices,
  /deep/ .stars {
    margin-top: 2px;
  }

  .labels {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    color: $red;
    .label {
      font-weight: 500;
      font-size: 10px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }
  }

  .brand {
    font-weight: 700;
    text-transform: uppercase;
  }

  .variants-count {
    color: $grey-darker;
    text-transform: lowercase;
  }
}

.product-card-ad {
  height: 45px;
}

.product-card-early-access {
  height: 20px;
  padding: 0px 8px;
  border-radius: 4px;
  width: fit-content;
  background-color: $red-70;

  span {
    font-family: Sephora Sans;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    color: $white;
  }
}
</style>
